import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { APPRoutes } from '../../Utils/Route';
import { formatUtcToLocalDateTime } from '../../Utils/DateTime';
import SearchableDropdown from '../../Components/Subscription/SearchableDropdown';
import DocumentUpload from '../../Components/Common/DocumentUpload';
import {
    saveHealthcarePayor,
    getHealthcarePayorById,
    getAllHealthCareNotes,
    saveHealthcareNote,
    getAllProviderNames,
    getAllProviderSettings,
} from '../../Services/ProviderService';
import { getAllOrganizationNames } from '../../Services/OrganizationService';
import { GetCountries, GetState } from 'react-country-state-city';
import { v4 as uuidv4 } from "uuid";
import { MdEdit, MdDelete } from 'react-icons/md';
import CustomDatePicker from '../../Components/Common/CustomDatePicker';
import CustomPhoneInput from '../../Components/Common/CustomPhoneInput';
import DotLottie from '../../Components/Common/DotLottie';

const EMPTY_FORM = {
    id: null,
    provider_id: '',
    status_id: '',
    file_id: null,
    file_name: '',
    practice: '',
    payor_name: '',
    category_plan: '',
    payor_description: '',
    payor_state: '',
    payor_status: '',
    effective_date: null,
    end_date: null,
    par_status: '',
    revalidation_date: null,
    application_submission_date: null,
    application_resubmission_date: null,
    approval_date: null,
    contact_id: null,
    contact: {
        id: null,
        contact_name: '',
        contact_title: '',
        contact_email: '',
        contact_phone: '',
        contact_fax: '',
    },
    type: 'create',
    document: null,
    speciality: '',
};

const validationSchema = Yup.object().shape({
    payor_name: Yup.string().trim().required('Payor Name is required'),
    provider_id: Yup.string().trim().nullable(),
    status_id: Yup.string().trim().nullable(),
    payor_status: Yup.string().trim().nullable(),
    par_status: Yup.string().trim().nullable(),
    effective_date: Yup.date().nullable(),
    end_date: Yup.date().nullable(),
    revalidation_date: Yup.date().nullable(),
    application_submission_date: Yup.date().nullable(),
    application_resubmission_date: Yup.date().nullable(),
    approval_date: Yup.date().nullable(),
    contact: Yup.object({
        contact_name: Yup.string().trim().nullable(),
        contact_title: Yup.string().trim().nullable(),
        contact_email: Yup.string().trim().email('Invalid email').nullable(),
        contact_phone: Yup.string().trim().nullable(),
        contact_fax: Yup.string().trim().nullable(),
    }).nullable(),
    speciality: Yup.string().trim().nullable(),
});

const normalizeArray = (value) => (Array.isArray(value) ? value : []);

const NOTES_PER_PAGE = 2;

const normalizeNoteMessages = (value) => {
    if (Array.isArray(value)) {
        return value.filter((entry) => entry && typeof entry === 'object');
    }

    if (value && typeof value === 'object') {
        return [value];
    }

    return [];
};

const mapApiToForm = (data) => ({
    id: data?.id ?? null,
    organization_id: data?.organization_id ?? data?.OrganizationId ?? null,
    provider_id: data?.provider_id ?? data?.ProviderId ?? data?.providerId ?? '',
    status_id: data?.status_id ?? data?.StatusId ?? '',
    file_id: data?.file_id ?? data?.FileId ?? null,
    practice: data?.practice ?? '',
    payor_name: data?.payor_name ?? data?.payorName ?? '',
    category_plan: data?.category_plan ?? data?.categoryPlan ?? '',
    payor_description: data?.payor_description ?? data?.payorDescription ?? '',
    payor_state: data?.payor_state ?? data?.payorState ?? '',
    payor_status: data?.payor_status ?? data?.payorStatus ?? '',
    effective_date: data?.effective_date ?? data?.effectiveDate ?? '',
    end_date: data?.end_date ?? data?.endDate ?? '',
    par_status: data?.par_status ?? data?.parStatus ?? '',
    revalidation_date: data?.revalidation_date ?? data?.revalidationDate ?? '',
    application_submission_date:
        data?.application_submission_date ?? data?.applicationSubmissionDate ?? '',
    application_resubmission_date:
        data?.application_resubmission_date ?? data?.applicationResubmissionDate ?? '',
    approval_date: data?.approval_date ?? data?.approvalDate ?? '',
    contact_id: data?.contact_id ?? data?.contactId ?? null,
    contact: {
        id: data?.contact?.id ?? data?.contact_id ?? data?.contactId ?? null,
        contact_name:
            data?.contact?.contact_name ?? data?.contact_name ?? data?.contactName ?? '',
        contact_title:
            data?.contact?.contact_title ?? data?.contact_title ?? data?.contactTitle ?? '',
        contact_email:
            data?.contact?.contact_email ?? data?.contact_email ?? data?.contactEmail ?? '',
        contact_phone:
            data?.contact?.contact_phone ?? data?.contact_phone ?? data?.contactPhone ?? '',
        contact_fax:
            data?.contact?.contact_fax ?? data?.contact_fax ?? data?.contactFax ?? '',
    },
    type: data?.type ?? (data?.id ? 'update' : 'create'),
    file_name: data?.file_name ?? data?.fileName ?? data?.FileName ?? null,
    document: data?.document ?? (data?.file_name ? { file_name: data?.file_name } : null),
    speciality: data?.speciality ?? data?.Speciality ?? '',
});

export default function HealthcarePayorsForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);
    const { token, permissions, decodedToken } = useAuth();
    const userPermissionCodes = React.useMemo(
        () => resolveUserPermissionCodes({ permissions, decodedToken }),
        [permissions, decodedToken]
    );
    const requiredPermission = isEdit ? PermissionCodes.HEALTHCARE_PAYOR_EDIT : PermissionCodes.HEALTHCARE_PAYOR_CREATE;
    const canAccessPage = permissionHas(userPermissionCodes, requiredPermission, decodedToken);

    const currentUsername = decodedToken?.username || decodedToken?.name || decodedToken?.sub || null;

    const [loading, setLoading] = React.useState(isEdit);
    const [submitLoading, setSubmitLoading] = React.useState(false);
    const [providerNames, setProviderNames] = React.useState([]);
    const [organizationNames, setOrganizationNames] = React.useState([]);
    const [settingOptions, setSettingOptions] = React.useState({
        payor_status: [],
        par_status: [],
    });
    const [notes, setNotes] = React.useState([]);
    const [noteText, setNoteText] = React.useState('');
    const [editingNoteIndex, setEditingNoteIndex] = React.useState(null);
    const [editingNoteGuid, setEditingNoteGuid] = React.useState(null);
    const [editingNoteText, setEditingNoteText] = React.useState('');
    const [noteSaving, setNoteSaving] = React.useState(false);
    const [notesLoading, setNotesLoading] = React.useState(false);
    const [payorStateOptions, setPayorStateOptions] = React.useState([]);
    const [payorStateLoading, setPayorStateLoading] = React.useState(false);

    const flattenedMessages = React.useMemo(() => {
        const messages = notes.reduce((acc, note) => {
            const msgs = normalizeNoteMessages(note?.notes);
            msgs.forEach((m) => {
                acc.push({
                    ...m,
                    _meta: {
                        created_by: note.created_by,
                        created_on: note.created_on,
                        noteId: note.id,
                    },
                });
            });
            return acc;
        }, []);

        return messages.sort((a, b) => {
            const aTime = new Date(a.utc || a._meta?.created_on || 0).getTime() || 0;
            const bTime = new Date(b.utc || b._meta?.created_on || 0).getTime() || 0;
            return bTime - aTime;
        });
    }, [notes]);

    const canModifyNote = (message) => {
        const noteAuthor = message?.by || null;
        return noteAuthor != null && currentUsername != null && String(noteAuthor).trim() === String(currentUsername).trim();
    };

    const formik = useFormik({
        initialValues: EMPTY_FORM,
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values) => {
            try {
                setSubmitLoading(true);

                const payload = {
                    ...values,
                    type: values.type || (isEdit ? 'update' : 'create'),
                    provider_id: values.provider_id || null,
                    status_id: values.status_id || null,
                    payor_status: values.payor_status || null,
                    par_status: values.par_status || null,
                    file_id: values.file_id || null,
                    contact: {
                        ...values.contact,
                    },
                };

                const response = await saveHealthcarePayor(token, payload);

                if (response?.is_success) {
                    toast.success(response?.message || 'Healthcare payor saved successfully');
                    navigate(APPRoutes.HEALTHCARE_PAYOR_VIEW.replace(':id', response?.data?.id || id));
                } else if (response?.status_code === 500) {
                    toast.error('Internal Server Error. Please Contact Support Team.');
                } else {
                    toast.error(response?.message || 'Failed to save healthcare payor');
                }
            } catch (error) {
                console.error('Error saving healthcare payor:', error);
                toast.error(error?.message || 'Failed to save healthcare payor');
            } finally {
                setSubmitLoading(false);
            }
        },
    });

    const loadDropdownData = async () => {
        try {
            const [providersResponse, organizationsResponse, statusResponse, parResponse] = await Promise.all([
                getAllProviderNames(token),
                getAllOrganizationNames(token),
                getAllProviderSettings(token, 38),
                getAllProviderSettings(token, 39),
            ]);

            setProviderNames(normalizeArray(providersResponse?.data ?? providersResponse));
            setOrganizationNames(normalizeArray(organizationsResponse?.data ?? organizationsResponse));
            setSettingOptions({
                payor_status: normalizeArray(statusResponse?.data ?? statusResponse),
                par_status: normalizeArray(parResponse?.data ?? parResponse),
            });
        } catch (error) {
            console.error('Failed to load dropdown data:', error);
        }
    };

    const loadPayor = async () => {
        if (!isEdit) return;
        try {
            setLoading(true);
            const response = await getHealthcarePayorById(token, id);
            if (response?.is_success) {
                formik.setValues(mapApiToForm(response?.data ?? response));
            }
        } catch (error) {
            console.error('Error loading healthcare payor:', error);
            toast.error('Failed to load healthcare payor details');
        } finally {
            setLoading(false);
        }
    };

    const loadNotes = async () => {
        if (!isEdit) return;

        try {
            setNotesLoading(true);
            const response = await getAllHealthCareNotes(token, id);
            setNotes(normalizeArray(response?.data ?? response).map(ensureNoteRecordGuids));
        } catch (error) {
            console.error('Error loading healthcare payor notes:', error);
            setNotes([]);
        }
        finally {
            setNotesLoading(false);
        }
    };

    React.useEffect(() => {
        if (!token) return;
        loadDropdownData();
        loadPayorStateOptions();
    }, [token]);

    const loadPayorStateOptions = async () => {
        try {
            setPayorStateLoading(true);
            const countries = await GetCountries();
            const unitedStates = Array.isArray(countries)
                ? countries.find((country) =>
                    ['united states', 'us', 'usa'].includes(String(country.name ?? '').trim().toLowerCase()) ||
                    String(country.iso2 ?? '').toUpperCase() === 'US' ||
                    String(country.iso3 ?? '').toUpperCase() === 'USA'
                )
                : null;
            const countryId = unitedStates?.id ?? unitedStates?.iso2 ?? unitedStates?.iso3 ?? 'US';
            const states = await GetState(countryId);
            if (Array.isArray(states)) {
                setPayorStateOptions(
                    states.map((s) => ({ value: s.name ?? s.state_code ?? s.code ?? '', label: s.name ?? s.state_code ?? s.code ?? '' }))
                );
            }
        } catch (error) {
            console.error('Failed to load payor state options:', error);
            setPayorStateOptions([]);
        } finally {
            setPayorStateLoading(false);
        }
    };

    React.useEffect(() => {
        if (!token || !isEdit) return;
        loadPayor();
        loadNotes();
    }, [token, id, isEdit]);

    const handleDocumentChange = (file) => {
        formik.setFieldValue('document', file);
    };

    const getCurrentNoteRecord = () => (Array.isArray(notes) && notes.length > 0 ? notes[0] : null);

    const assignMessageGuid = (message) => {
        if (!message || typeof message !== 'object') {
            return message;
        }

        if (message.guid) {
            return message;
        }

        return {
            ...message,
            guid: message.guid ?? message.note_guid ?? uuidv4(),
        };
    };

    const ensureNoteRecordGuids = (record) => {
        if (!record || typeof record !== 'object') {
            return null;
        }

        return {
            ...record,
            notes: normalizeNoteMessages(record.notes).map(assignMessageGuid),
        };
    };

    const buildSaveNotePayload = (updatedMessages) => {
        const noteRecord = getCurrentNoteRecord();
        const authorId = decodedToken?.sub || decodedToken?.user_id || decodedToken?.id || null;

        return {
            id: noteRecord?.id ?? null,
            provider_id: formik.values.provider_id || null,
            healthcarepayor_id: id,
            created_by: noteRecord?.created_by ?? authorId,
            notes: updatedMessages,
            type: noteRecord ? 'update' : 'create',
        };
    };

    const handleSaveEditedNote = async () => {
        if (editingNoteIndex == null || editingNoteText == null) {
            return;
        }

        const noteRecord = ensureNoteRecordGuids(getCurrentNoteRecord());
        if (!noteRecord) {
            toast.error('No existing note record available to edit.');
            return;
        }

        const existingMessages = normalizeNoteMessages(noteRecord.notes);
        if (!existingMessages[editingNoteIndex]) {
            toast.error('Note not found.');
            return;
        }

        const trimmedText = String(editingNoteText).trim();
        if (!trimmedText) {
            toast.error('Please enter note text before saving.');
            return;
        }

        const updatedMessages = existingMessages.map((message, idx) => {
            const isMatch = message.guid
                ? message.guid === editingNoteGuid
                : idx === editingNoteIndex;

            return isMatch
                ? { ...message, msg: trimmedText, utc: new Date().toISOString(), guid: message.guid ?? editingNoteGuid ?? uuidv4() }
                : message;
        });

        const updatedNoteRecord = { ...noteRecord, notes: updatedMessages };
        setNotes([{ ...updatedNoteRecord }]);
        setEditingNoteIndex(null);
        setEditingNoteGuid(null);
        setEditingNoteText('');

        try {
            const payload = buildSaveNotePayload(updatedMessages);
            const response = await saveHealthcareNote(token, payload);
            if (!response?.is_success & response.status_code === 500) {
                toast.error('Internal Server Error. Please Contact Support Team');
            } else if (!response?.is_success) {
                toast.error(response?.message || 'Failed to save notes')
            }
        } catch (error) {
            console.error('Error updating note:', error);
            toast.error(error?.message || 'Failed to update note');
        } finally {
            //setNoteSaving(false);
        }
    };

    const handleDeleteNote = async (deleteIndex) => {
        const noteRecord = ensureNoteRecordGuids(getCurrentNoteRecord());
        if (!noteRecord) {
            toast.error('No existing note record available to delete.');
            return;
        }

        const existingMessages = normalizeNoteMessages(noteRecord.notes);
        if (!existingMessages[deleteIndex]) {
            toast.error('Note not found.');
            return;
        }

        const deletedGuid = existingMessages[deleteIndex].guid;
        const updatedMessages = existingMessages.filter((message, idx) => {
            if (message.guid) {
                return message.guid !== deletedGuid;
            }
            return idx !== deleteIndex;
        });
        const updatedNotes = updatedMessages.length > 0 ? [{ ...noteRecord, notes: updatedMessages }] : [];

        setNotes(updatedNotes);
        if (editingNoteIndex === deleteIndex) {
            setEditingNoteIndex(null);
            setEditingNoteGuid(null);
            setEditingNoteText('');
        }

        try {
            const payload = buildSaveNotePayload(updatedMessages);
            const response = await saveHealthcareNote(token, payload);
            if (!response?.is_success) {
                toast.error(response?.message || 'Failed to delete note');
            }
        } catch (error) {
            console.error('Error deleting note:', error);
            toast.error(error?.message || 'Failed to delete note');
        }
    };

    const handleEditNoteClick = (message, index) => {
        setEditingNoteIndex(index);
        setEditingNoteGuid(message.guid ?? null);
        setEditingNoteText(message.msg || '');
    };

    const handleCancelEdit = () => {
        setEditingNoteIndex(null);
        setEditingNoteGuid(null);
        setEditingNoteText('');
    };

    const handleSaveNote = async () => {
        if (!noteText.trim()) {
            toast.error('Please enter a note before saving.');
            return;
        }

        const authorId = decodedToken?.sub || decodedToken?.user_id || decodedToken?.id || null;
        const authorName = decodedToken?.username || decodedToken?.name || decodedToken?.sub || 'Unknown';

        const existing = ensureNoteRecordGuids(Array.isArray(notes) && notes.length > 0 ? notes[0] : null);
        const existingMessages = normalizeNoteMessages(existing?.notes);
        const newEntry = {
            guid: uuidv4(),
            msg: noteText.trim(),
            by: authorName,
            utc: new Date().toISOString(),
        };
        const combinedMessages = [...existingMessages, newEntry];

        const payload = {
            id: existing?.id ?? null,
            provider_id: formik.values.provider_id || null,
            healthcarepayor_id: id,
            created_by: existing?.created_by ?? authorId,
            notes: combinedMessages,
            type: existing ? 'update' : 'create',
        };

        try {
            setNoteSaving(true);
            const response = await saveHealthcareNote(token, payload);
            if (response?.is_success) {
                const responseData = response?.data ?? response;
                const updatedNotes = responseData
                    ? [{
                        id: responseData.id ?? existing?.id ?? null,
                        created_by: responseData.created_by ?? existing?.created_by ?? authorId,
                        notes: normalizeNoteMessages(responseData.notes ?? existingMessages.concat(newEntry)),
                    }]
                    : [{
                        ...existing,
                        notes: [...existingMessages, newEntry],
                    }];
                setNoteText('');
                setNotes(updatedNotes);
                toast.success(response?.message || 'Note saved successfully');
            } else {
                toast.error(response?.message || 'Failed to save note');
            }
        } catch (error) {
            console.error('Error saving note:', error);
            toast.error(error?.message || 'Failed to save note');
        } finally {
            setNoteSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center min-vh-100">
                <DotLottie />
            </div>
        );
    }

    const providerOptions = providerNames.map((provider) => ({
        value: provider.id ?? provider.Id ?? provider.value ?? '',
        label: provider.name ?? provider.ProviderName ?? provider.provider_name ?? provider.label ?? '',
    }));

    const payorStatusOptions = settingOptions.payor_status.map((item) => ({
        value: item.id ?? item.Id ?? item.value ?? item.code ?? '',
        label: item.name ?? item.Name ?? item.label ?? String(item.id ?? item.Id ?? ''),
    }));

    const parStatusOptions = settingOptions.par_status.map((item) => ({
        value: item.id ?? item.Id ?? item.value ?? item.code ?? '',
        label: item.name ?? item.Name ?? item.label ?? String(item.id ?? item.Id ?? ''),
    }));

    if (!canAccessPage) {
        return (
            <div className="container-fluid py-4">
                <div className="alert alert-danger">You do not have permission to access this page.</div>
            </div>
        );
    }

    return (
        <div className="container-fluid p-0">
            <div className="rounded-4 bg-white p-2 p-md-4 shadow-md border-0">
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div className="d-flex align-items-center gap-3">
                        <button
                            type="button"
                            className="btn btn-light border rounded-circle shadow-sm"
                            onClick={() => navigate(-1)}
                            style={{ width: 40, height: 40 }}
                        >
                            &larr;
                        </button>
                        <div>
                            <h3 className="fw-bold mb-0 hc-text-primary">
                                {isEdit ? 'Edit Healthcare Payor' : 'Add Healthcare Payor'}
                            </h3>
                            <small className="text-muted">
                                {isEdit
                                    ? 'Update the healthcare payor details below.'
                                    : 'Create a new healthcare payor entry for credentialing.'}
                            </small>
                        </div>
                    </div>
                </div>

                <form onSubmit={formik.handleSubmit}>
                    <div className="rounded-4 border p-3 p-md-4 bg-white shadow-sm mb-4">
                        <h6 className="fw-bold hc-text-primary mb-3">HealthCare Payor Information</h6>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">Payor Name</label>
                                <input
                                    name="payor_name"
                                    className="form-control rounded-3 shadow-sm"
                                    value={formik.values.payor_name}
                                    onChange={formik.handleChange}
                                    placeholder="Enter payor name"
                                />
                            </div>

                            <div className="col-md-6">
                                <SearchableDropdown
                                    label="Practice"
                                    placeholder="Select Option"
                                    options={organizationNames.map((org) => ({
                                        value: org.id ?? org.Id ?? org.value ?? '',
                                        label: org.name ?? org.group_office_name ?? org.organization_name ?? org.label ?? '',
                                    }))}
                                    value={formik.values.organization_id}
                                    onChange={(value) => formik.setFieldValue('organization_id', value || '')}
                                    loading={organizationNames.length === 0}
                                    emptyMessage="No organizations found"
                                    className="rounded-3"
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label fw-semibold">Category / Plan</label>
                                <input
                                    name="category_plan"
                                    className="form-control rounded-3 shadow-sm"
                                    value={formik.values.category_plan}
                                    onChange={formik.handleChange}
                                    placeholder="Enter category or plan"
                                />
                            </div>

                            <div className="col-md-6">
                                <SearchableDropdown
                                    label="Payor State"
                                    placeholder="Select Option"
                                    options={payorStateOptions}
                                    value={formik.values.payor_state}
                                    onChange={(value) => formik.setFieldValue('payor_state', value || '')}
                                    loading={payorStateLoading}
                                    emptyMessage="No states found"
                                    className="rounded-3"
                                    allowCustom={true}
                                />
                            </div>

                            <div className="col-md-6">
                                <SearchableDropdown
                                    label="Payor Status"
                                    placeholder="Select Option"
                                    options={payorStatusOptions}
                                    value={formik.values.payor_status}
                                    onChange={(value) => formik.setFieldValue('payor_status', value || '')}
                                    loading={payorStatusOptions.length === 0}
                                    emptyMessage="No payor statuses found"
                                    className="rounded-3"
                                />
                            </div>

                            <div className="col-md-6">
                                <SearchableDropdown
                                    label="PAR Status"
                                    placeholder="Select Option"
                                    options={parStatusOptions}
                                    value={formik.values.par_status}
                                    onChange={(value) => formik.setFieldValue('par_status', value || '')}
                                    loading={parStatusOptions.length === 0}
                                    emptyMessage="No PAR statuses found"
                                    className="rounded-3"
                                />
                            </div>

                            <div className="col-md-6">
                                <SearchableDropdown
                                    label="Provider"
                                    placeholder="Select Option"
                                    options={providerOptions}
                                    value={formik.values.provider_id}
                                    onChange={(value) => formik.setFieldValue('provider_id', value || '')}
                                    loading={providerOptions.length === 0}
                                    emptyMessage="No providers found"
                                    className="rounded-3"
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label fw-semibold">Speciality</label>
                                <select
                                    name="speciality"
                                    className="form-select rounded-3 shadow-sm"
                                    value={formik.values.speciality}
                                    onChange={formik.handleChange}
                                >
                                    <option value="">Select Speciality</option>
                                    <option value="mental/behavioural">Mental/Behavioural</option>
                                    <option value="dental">Dental</option>
                                    <option value="medical">Medical</option>
                                    <option value="vision">Vision</option>
                                </select>
                            </div>

                            <div className="col-12">
                                <label className="form-label fw-semibold">Description</label>
                                <textarea
                                    name="payor_description"
                                    className="form-control rounded-3 shadow-sm"
                                    rows={3}
                                    value={formik.values.payor_description}
                                    onChange={formik.handleChange}
                                    placeholder="Enter payor description"
                                />
                            </div>

                            {['effective_date', 'end_date', 'revalidation_date', 'application_submission_date', 'application_resubmission_date', 'approval_date'].map((field) => (
                                <div className="col-md-6" key={field}>
                                    <label className="form-label fw-semibold text-capitalize">
                                        {field.replace(/_/g, ' ').replace(/date/i, 'Date')}
                                    </label>
                                    <CustomDatePicker
                                        name={field}
                                        formik={formik}
                                    />
                                    {/* <input
                                        name={field}
                                        type="date"
                                        className="form-control rounded-3 shadow-sm"
                                        value={formik.values[field] || ''}
                                        onChange={formik.handleChange}
                                    /> */}
                                </div>
                            ))}
                        </div>
                    </div>

                    {isEdit && (
                        <div className="rounded-4 border p-3 p-md-4 bg-white shadow-sm mb-4">
                            <h6 className="fw-bold hc-text-primary mb-3">Notes</h6>

                            <div
                                className="border rounded-4 p-3 mb-3 bg-light overflow-y-auto"
                                style={{ maxHeight: "280px" }}
                            >
                                {notesLoading ? (
                                    <div className="row g-3">
                                        {Array.from({ length: NOTES_PER_PAGE }).map((_, idx) => (
                                            <div key={idx} className="col-12">
                                                <div className="h-100 rounded-4 border bg-white px-3 py-3">
                                                    <div className="skeleton mb-2 rounded-2" style={{ height: 14, width: '35%' }} />
                                                    <div className="skeleton mb-2 rounded-2" style={{ height: 12, width: '20%' }} />
                                                    <div className="skeleton mb-2 rounded-2" style={{ height: 10, width: '90%' }} />
                                                    <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 10, width: '80%' }} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : flattenedMessages.length === 0 ? (
                                    <div className="text-secondary small">
                                        No notes yet.
                                    </div>
                                ) : (
                                    flattenedMessages.map((message, idx) => {
                                        const isEditing = editingNoteIndex === idx;

                                        return (
                                            <div
                                                key={
                                                    message?._meta?.noteId
                                                        ? `${message._meta.noteId}-${idx}`
                                                        : idx
                                                }
                                                className="mb-3 pb-3 border-bottom rounded-4 bg-white p-3 shadow"
                                            >
                                                <div className="d-flex flex-md-row flex-column justify-content-between align-items-start mb-2">
                                                    <div className="fw-semibold">
                                                        {message.by ||
                                                            message?._meta?.created_by ||
                                                            "Unknown"}
                                                    </div>

                                                    <div className="text-secondary small d-flex flex-md-row flex-column align-items-md-center align-items-start gap-2">
                                                        <div className="me-3">
                                                            {formatUtcToLocalDateTime(
                                                                message.utc ||
                                                                message?._meta?.created_on ||
                                                                new Date()
                                                            )}
                                                        </div>
                                                        {canModifyNote(message) && (
                                                            <div className="d-flex justify-content-end gap-2">
                                                                <button
                                                                    type="button"
                                                                    title="Edit"
                                                                    className="btn btn-sm p-1 lh-1 hc-text-primary border-0 rounded-2"
                                                                    style={{ backgroundColor: 'var(--hc-icon-blue-bg)' }}
                                                                    onClick={() => handleEditNoteClick(message, idx)}
                                                                >
                                                                    <MdEdit size={16} />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    title="Delete"
                                                                    className="btn btn-sm p-1 lh-1 text-danger border-0 rounded-2"
                                                                    style={{ backgroundColor: 'var(--hc-status-danger-bg)' }}
                                                                    onClick={() => handleDeleteNote(idx)}
                                                                >
                                                                    <MdDelete size={16} />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>

                                                </div>

                                                {isEditing ? (
                                                    <>
                                                        <textarea
                                                            className="form-control rounded-3 shadow-sm"
                                                            rows={4}
                                                            value={editingNoteText}
                                                            onChange={(e) => setEditingNoteText(e.target.value)}
                                                            placeholder="Edit note..."
                                                        />
                                                        <div className="d-flex justify-content-end gap-2 mt-2">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-primary"
                                                                onClick={handleSaveEditedNote}
                                                            >
                                                                Save
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-secondary"
                                                                onClick={handleCancelEdit}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div style={{ whiteSpace: "pre-wrap" }}>
                                                            {message.msg || ""}
                                                        </div>

                                                    </>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            <div className="mb-3">
                                <textarea
                                    className="form-control rounded-3 shadow-sm"
                                    rows={6}
                                    value={noteText}
                                    onChange={(e) => setNoteText(e.target.value)}
                                    placeholder="Write a note..."
                                />
                            </div>

                            <div className="d-flex justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-sm hc-bg-primary text-white"
                                    onClick={handleSaveNote}
                                    disabled={noteSaving}
                                >
                                    {noteSaving ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                                aria-hidden="true"
                                            ></span>
                                            {notes.length > 0 ? "Updating..." : "Saving..."}
                                        </>
                                    ) : (
                                        notes.length > 0 ? "Update Note" : "Save Note"
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="rounded-4 border p-3 p-md-4 bg-white shadow-sm mb-4">
                        <h6 className="fw-bold hc-text-primary mb-3">Contact Information</h6>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">Contact Name</label>
                                <input
                                    name="contact.contact_name"
                                    className="form-control rounded-3 shadow-sm"
                                    value={formik.values.contact.contact_name}
                                    onChange={formik.handleChange}
                                    placeholder="Enter contact name"
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">Contact Title</label>
                                <input
                                    name="contact.contact_title"
                                    className="form-control rounded-3 shadow-sm"
                                    value={formik.values.contact.contact_title}
                                    onChange={formik.handleChange}
                                    placeholder="Enter contact title"
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">Contact Email</label>
                                <input
                                    name="contact.contact_email"
                                    type="email"
                                    className="form-control rounded-3 shadow-sm"
                                    value={formik.values.contact.contact_email}
                                    onChange={formik.handleChange}
                                    placeholder="Enter contact email"
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">Contact Phone</label>

                                   <CustomPhoneInput
                                   formik={formik}
                                   name="contact.contact_phone"
                                   placeholder="Enter contact phone"
                                   /> 

                                {/* <input
                                    name="contact.contact_phone"
                                    className="form-control rounded-3 shadow-sm"
                                    value={formik.values.contact.contact_phone}
                                    onChange={formik.handleChange}
                                    placeholder="Enter contact phone"
                                /> */}
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">Contact Fax</label>
                                <input
                                    name="contact.contact_fax"
                                    className="form-control rounded-3 shadow-sm"
                                    value={formik.values.contact.contact_fax}
                                    onChange={formik.handleChange}
                                    placeholder="Enter contact fax"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="rounded-4 border p-3 p-md-4 bg-white shadow-sm mb-4">
                        <h6 className="fw-bold hc-text-primary mb-3">Document</h6>
                        <DocumentUpload
                            label="Upload document"
                            value={formik.values.file_name || (formik.values.file_id ? { file_name: 'Existing document attached' } : null)}
                            onChange={handleDocumentChange}
                        />
                    </div>

                    <div className="d-flex justify-content-end gap-2">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => navigate(APPRoutes.HEALTHCARE_PAYOR)}
                        >
                            Cancel
                        </button>
                        <button type="submit" className="btn hc-bg-primary text-white" disabled={submitLoading}>
                            {submitLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                    Saving...
                                </>
                            ) : (
                                isEdit ? 'Save Changes' : 'Save Payor'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
